import { memo, useCallback, useState, useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Building2Icon, CheckIcon, PlusIcon, SettingsIcon, UserRoundPlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { NotificationBadge } from '@/components/atoms/NotificationBadge/NotificationBadge';
import { MODAL_FORM, MODAL_MEMBER_INVITE } from '@/components/organisms/Modals/ModalConductor';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge as ShadcnBadge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useAccount } from '@/hooks/useAccount';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { useInvitations } from '@/hooks/useInvitations';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { initDeskBrainIntegration } from '@/modules/onboarding/helper';
import { popModal, pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setAccountId } from '@/redux/session/actions';
import { trackEvent } from '@/segment/segment';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { accountRules } from '@/util/validation/rules/entity-rules';

export const getSwitchAccountLink = (newSlug: string, oldSlug: string) => {
  const oldUrl = window.location.pathname;
  const paths = [
    'brains',
    'ai-agents',
    'collections',
    'broadcasts',
    'campaigns',
    'integrations',
    'rules',
    'environments',
    'chats',
  ];

  for (const path of paths) {
    if (oldUrl.includes(path)) {
      return `/${newSlug}/${path}`;
    }
  }
  return oldUrl.replace(oldSlug, newSlug);
};

export const AccountMenu = memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { handleInvisibleBadge } = useInvitations();
  const dispatch = useDispatch();
  const { createAccount, account, accounts, slug, switchAccount } = useAccount();
  const queryClient = useQueryClient();
  const { billingState } = useBilling();
  const [open, setOpen] = useState(false);
  const [isSwitchAccountOpen, setIsSwitchAccountOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const subContentRef = useRef<HTMLDivElement>(null);
  const deskId = useDeskId();
  const { desk } = useDesks(deskId);

  const focusSearchInput = useCallback(() => {
    const input = subContentRef.current?.querySelector<HTMLInputElement>(
      '[data-slot="command-input"]'
    );
    input?.focus();
  }, []);

  const handleSubMenuOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsSwitchAccountOpen(isOpen);
      if (isOpen) {
        // For keyboard navigation - requestAnimationFrame to let Radix settle
        requestAnimationFrame(() => focusSearchInput());
      }
    },
    [focusSearchInput]
  );

  // Permissions

  const canAssign = useSelector((state: RootState) =>
    getPermissions(state, 'roles', actions.ASSIGN)
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setIsSwitchAccountOpen(false);
    setSearchTerm(''); // Reset search when menu closes
  }, []);

  const handleSelection = useCallback(
    (newSlug, showSettings) => {
      handleClose();

      if (newSlug !== account?.slug) {
        switchAccount(newSlug);
      }

      if (showSettings) {
        navigate(`/${newSlug}/account/information`);
        return;
      }
      const oldUrl = window.location.pathname;

      if (!oldUrl.includes(account?.slug)) {
        return;
      }
      navigate(getSwitchAccountLink(newSlug, account?.slug));
    },
    [account?.slug, handleClose, navigate, switchAccount]
  );

  const handleCreateAccount = useCallback(
    async (data) => {
      createAccount(data, {
        onSuccess: async (newAccount) => {
          dispatch(setAccountId(newAccount.account_id));

          handleSelection(newAccount.slug, false);

          await initDeskBrainIntegration({ accountId: newAccount.account_id, queryClient });

          dispatch(popModal());
        },
      });
    },
    [createAccount, dispatch, handleSelection, queryClient]
  );

  const showCreateAccount = useCallback(() => {
    trackEvent(EventName.ClickCreateAccount);
    const renameProps = {
      resource: t('accounts.new'),
      fields: [{ fieldName: 'name', fieldValue: '', rules: accountRules.name }],

      onCreate: handleCreateAccount,
    };
    dispatch(pushModal(MODAL_FORM, renameProps));
  }, [t, handleCreateAccount, dispatch]);

  const handleAccountSelection = useCallback(
    (slug: string) => {
      handleSelection(slug, false);
    },
    [handleSelection]
  );

  const settingsPath = `/${account?.slug}/account/information`;

  const handleMemberInviteClick = useCallback(() => {
    dispatch(pushModal(MODAL_MEMBER_INVITE));
    handleClose();
  }, [dispatch, handleClose]);

  const handleCreateAccountClick = useCallback(() => {
    showCreateAccount();
    handleClose();
  }, [showCreateAccount, handleClose]);

  const filteredAccounts = useMemo(() => {
    if (!accounts || !searchTerm.trim()) {
      return accounts || [];
    }

    return accounts.filter((account) =>
      account.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [accounts, searchTerm]);

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <InfoTooltip content={t('main_navigation.account_menu')} delayDuration={200}>
          <DropdownMenuTrigger asChild>
            <Button
              id="account-button"
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              aria-label={t('main_navigation.account_menu')}
              className="h-8 w-8 min-w-0 rounded p-0 hover:bg-white/16 xl:h-9 xl:w-auto xl:max-w-64 xl:px-2 xl:py-1.5"
              data-testid="account-menu"
              variant="ghost"
            >
              <>
                <NotificationBadge
                  badgeContent={!handleInvisibleBadge?.invisible}
                  className="flex min-w-0 items-center gap-2"
                >
                  <Avatar size="xxs">
                    <AvatarImage src={account?.avatar || ''} alt={`${account?.name}'s avatar`} />
                    <AvatarFallback>{account?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>

                  {account ? (
                    <>
                      <span className="hidden min-w-0 truncate text-sm leading-6 font-medium text-primary-foreground xl:inline">
                        {account?.name}
                      </span>

                      {billingState && (
                        // The nav bar stays blue in both themes, so pin the border to slate-200.
                        <ShadcnBadge
                          className="hidden border-[#E2E8F0] text-primary-foreground xl:block"
                          type="outline"
                          flavour="neutral"
                          rounded={false}
                        >
                          {billingState?.plan_name}
                        </ShadcnBadge>
                      )}
                    </>
                  ) : (
                    <Skeleton className="hidden h-4 w-[120px] xl:block" />
                  )}
                </NotificationBadge>
              </>
            </Button>
          </DropdownMenuTrigger>
        </InfoTooltip>
        <DropdownMenuContent
          className="w-56"
          align="start"
          sideOffset={0}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-2 px-2 py-1.5">
            <img
              src={account?.avatar}
              alt={account?.name}
              className="h-8 w-8 rounded-lg bg-sidebar"
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <p className="truncate text-sm font-semibold text-foreground">{account?.name}</p>
              <p className="truncate text-xs text-foreground-muted">{desk?.name}</p>
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem asChild>
              <Link to={settingsPath} className="flex items-center gap-2 text-inherit no-underline">
                <SettingsIcon size={16} className="text-foreground-muted" />
                {t('common.settings')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={handleMemberInviteClick}
              disabled={!canAssign}
            >
              <UserRoundPlusIcon size={16} className="text-foreground-muted" />
              {t('members.invite_members')}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem
              onClick={handleCreateAccountClick}
              className="flex items-center gap-2"
            >
              <PlusIcon size={16} className="text-foreground-muted" />
              {t('common.new_account')}
            </DropdownMenuItem>

            <DropdownMenuSub onOpenChange={handleSubMenuOpenChange}>
              <DropdownMenuSubTrigger className="flex cursor-pointer items-center gap-2">
                <Building2Icon size={16} className="text-foreground-muted" />
                {t('common.switch_account')}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent
                  ref={subContentRef}
                  className="w-64 p-0"
                  onPointerEnter={focusSearchInput}
                >
                  {/* Ungated, a row per account is built on every render of the menu. */}
                  {isSwitchAccountOpen && (
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t('common.search_dots')}
                        value={searchTerm}
                        onValueChange={setSearchTerm}
                      />
                      <CommandList>
                        <CommandEmpty>{t('common.no_results')}</CommandEmpty>
                        <CommandGroup>
                          {filteredAccounts.map((acc) => {
                            const isCurrentAccount = acc.slug === slug;
                            return (
                              <CommandItem
                                key={acc.slug}
                                value={acc.slug}
                                onSelect={() => handleAccountSelection(acc.slug)}
                                className="p-0"
                              >
                                <Link
                                  to={getSwitchAccountLink(acc.slug, slug)}
                                  onClick={(e) => {
                                    if (e.metaKey || e.ctrlKey) {
                                      return;
                                    }
                                    e.preventDefault();
                                  }}
                                  className="flex w-full items-center gap-2 px-2 py-1.5 text-inherit no-underline"
                                >
                                  <Avatar size="xxs">
                                    <AvatarImage
                                      src={acc.avatar || ''}
                                      alt={`${acc.name}'s avatar`}
                                    />
                                    <AvatarFallback>{acc.name?.charAt(0)}</AvatarFallback>
                                  </Avatar>
                                  <span className="min-w-0 flex-1 truncate">{acc.name}</span>
                                  {isCurrentAccount && (
                                    <CheckIcon size={16} className="text-foreground-muted" />
                                  )}
                                </Link>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
});

AccountMenu.displayName = 'AccountMenu';
